import type { RowData, RowSelectionState, SortingState } from "@tanstack/react-table";
import React, { createContext, useContext } from "react";
import type { QueryKey, useQuery } from "../../query";
import type { ClassNameProps } from "../../types";
import type { DataTableVariant } from "./shared";
import type { DataTableInstance, Row } from "./types";

/** Interface */
export interface IDataTableContext<T extends RowData> {
	table: DataTableInstance<T>;
	persistenceKey: string;
	queryKey?: QueryKey;
	refetch: ReturnType<typeof useQuery>["refetch"] | undefined;
	tableContainerRef: React.RefObject<HTMLDivElement | null>;
	tableBoxRef: React.RefObject<HTMLDivElement | null>;
	columnSizes: Record<string, number>;
	hasRowSelection: boolean;
	rowSelection: RowSelectionState;
	itemRenderId: string;
	handleOnSetItemRenderId: () => void;
	getSelectedRowIds: () => string[];

	getRowStyles?: (row: Row<T>) => React.CSSProperties & ClassNameProps;
	injectRowFilter?: (row: Array<Row<T>>) => Array<Row<T>>;

	sorting: SortingState;
	isMonospaced: boolean;
	handleOnToggleMonospaced: () => void;

	showGroupingHeader: boolean;
	setShowGroupingHeader: React.Dispatch<React.SetStateAction<boolean>>;

	renderSubRows?: (row: Row<T>) => React.ReactNode;
	variant: DataTableVariant;
}

/**
 * Context — erased to `any` rows. TanStack's instance types are invariant in the row type, so
 * the concrete `T` a `DataTable` renders cannot flow through a React context; `DataTable` casts
 * once when it provides the value, and consumers read rows as `Row<any>`.
 */
export const DataTableContext = createContext({} as IDataTableContext<any>);

/** Hooks */
export function useDataTableContext() {
	return useContext(DataTableContext);
}
